<template>
  <Transition name="platforma-update-banner">
    <aside
      v-if="updateAvailable"
      class="platforma-update-banner"
      role="status"
      aria-live="polite"
    >
      <div>
        <strong>An update is ready</strong>
        <p>Refresh when you are ready. Your current work will not be interrupted automatically.</p>
      </div>
      <button
        type="button"
        @click="refreshApplication"
      >
        Refresh now
      </button>
    </aside>
  </Transition>
</template>

<script setup>
/* global __PLATFORMA_RELEASE_ID__ */
import { onBeforeUnmount, onMounted, ref } from 'vue';

const CHECK_INTERVAL_MS = 60_000;
const updateAvailable = ref(false);
const currentReleaseId = String(__PLATFORMA_RELEASE_ID__ || '').trim();
let checkTimer = null;
let checking = false;

async function checkForUpdate() {
  if (checking || !currentReleaseId || document.visibilityState === 'hidden') return;
  checking = true;

  try {
    const response = await fetch(`/platforma-release.json?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const release = await response.json();
    const deployedReleaseId = String(release?.releaseId || '').trim();
    if (deployedReleaseId && deployedReleaseId !== currentReleaseId) {
      updateAvailable.value = true;
    }
  } catch (_error) {
    // A temporary network interruption is not an update signal. Try again later.
  } finally {
    checking = false;
  }
}

function refreshApplication() {
  window.location.reload();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') void checkForUpdate();
}

onMounted(() => {
  window.setTimeout(() => { void checkForUpdate(); }, 15_000);
  checkTimer = window.setInterval(() => { void checkForUpdate(); }, CHECK_INTERVAL_MS);
  window.addEventListener('focus', checkForUpdate);
  window.addEventListener('online', checkForUpdate);
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  if (checkTimer) window.clearInterval(checkTimer);
  window.removeEventListener('focus', checkForUpdate);
  window.removeEventListener('online', checkForUpdate);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<style scoped>
.platforma-update-banner {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483001;
  display: flex;
  align-items: center;
  gap: 18px;
  width: min(480px, calc(100vw - 40px));
  padding: 16px 18px;
  border: 1px solid rgba(148, 163, 184, .26);
  border-radius: 17px;
  background: rgba(24, 24, 27, .96);
  box-shadow: 0 20px 60px rgba(0, 0, 0, .28);
  color: #fff;
  backdrop-filter: blur(16px);
}

.platforma-update-banner strong {
  display: block;
  font-size: 14px;
  line-height: 1.25;
}

.platforma-update-banner p {
  margin: 4px 0 0;
  color: #d4d4d8;
  font-size: 12px;
  line-height: 1.45;
}

.platforma-update-banner button {
  flex: none;
  border: 0;
  border-radius: 11px;
  padding: 10px 13px;
  background: #2d8cff;
  color: #fff;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
}

.platforma-update-banner button:focus-visible {
  outline: 3px solid rgba(147, 197, 253, .72);
  outline-offset: 2px;
}

.platforma-update-banner-enter-active,
.platforma-update-banner-leave-active {
  transition: opacity .2s ease, transform .2s ease;
}

.platforma-update-banner-enter-from,
.platforma-update-banner-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 640px) {
  .platforma-update-banner {
    right: 12px;
    bottom: 12px;
    width: calc(100vw - 24px);
    align-items: flex-start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .platforma-update-banner-enter-active,
  .platforma-update-banner-leave-active {
    transition: none;
  }
}
</style>
